import { useMemo, useState, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslations } from '@/i18n';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog';
import {
  useCreateTripCost,
  useDeleteTripCost,
  useUpdateTripCost,
} from '@/hooks/trips/useTripCostMutations';
import type { TripDetail, TripDetailCost, TripDetailCostDocument } from '@/hooks/trips/types';
import type { CostType, CostPayload } from '@/hooks/trips/useTripCostMutations';
import {
  useUploadDocuments,
  useDeleteDocument,
  useDocumentActions,
} from '@/hooks/useUploadDocuments';

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/jpg',
  'image/jpe',
];
const ACCEPTED_FILE_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png'];

interface CategoryConfig {
  labelKey: string;
  icon: string;
}

const DEFAULT_CURRENCY_ID = 80;

export type VisibleCostCategory = 'hotel' | 'transportation' | 'other';

const CATEGORY_CONFIG: Record<VisibleCostCategory, CategoryConfig> = {
  hotel: {
    labelKey: 'hotel',
    icon: '/assets/icons/building.svg',
  },
  transportation: {
    labelKey: 'transportation',
    icon: '/assets/icons/plane.svg',
  },
  other: {
    labelKey: 'other',
    icon: '/assets/icons/receipt.svg',
  },
};

const CATEGORY_ORDER: VisibleCostCategory[] = ['hotel', 'transportation', 'other'];

function mapApiCostType(value: string): VisibleCostCategory | null {
  switch (value) {
    case 'hotel':
      return 'hotel';
    case 'transportation':
      return 'transportation';
    case 'fuel':
    case 'daily':
    case 'other':
      return 'other';
    default:
      return null;
  }
}

interface CostFormState {
  mode: 'create' | 'edit';
  type: VisibleCostCategory;
  cost?: TripDetailCost;
}

interface TripCostsComparisonCardProps {
  tripId: string;
  trip: TripDetail;
  canManage: boolean;
  highlightCategories?: VisibleCostCategory[];
}

interface CostFormValues {
  comment: string;
}

interface UploadCandidate {
  file: File;
  id: string;
  error?: string;
}

function generateUploadId(file: File, index: number) {
  return `${file.name}-${file.size}-${file.lastModified}-${index}`;
}

function formatBytes(value?: number, fallback?: string) {
  if (value == null || Number.isNaN(value) || value <= 0) {
    return fallback ?? '—';
  }

  const units = ['B', 'KB', 'MB', 'GB'];
  let size = value;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

export function TripCostsComparisonCard({
  trip,
  tripId,
  canManage,
  highlightCategories,
}: TripCostsComparisonCardProps) {
  const t = useTranslations('trips.costComparison');
  const tCategories = useTranslations('trips.costComparison.categories');
  const tForm = useTranslations('trips.costComparison.form');
  const tErrors = useTranslations('trips.costComparison.errors');
  const createCostMutation = useCreateTripCost();
  const updateCostMutation = useUpdateTripCost();
  const deleteCostMutation = useDeleteTripCost();
  const uploadDocumentsMutation = useUploadDocuments();
  const deleteDocumentMutation = useDeleteDocument();
  const { viewDocument, downloadDocument, isDocumentLoading, isAnyDocumentLoading } =
    useDocumentActions();

  const costsByType = useMemo(() => {
    const grouped: Record<VisibleCostCategory, TripDetailCost[]> = {
      hotel: [],
      transportation: [],
      other: [],
    };

    (trip.costs ?? []).forEach((cost) => {
      const mapped = mapApiCostType(cost.costType);
      if (mapped) {
        grouped[mapped].push(cost);
      }
    });

    return grouped;
  }, [trip.costs]);

  const requiredTypes = useMemo(() => {
    const types = new Set<VisibleCostCategory>();

    if (trip.cities?.some((city) => city.needHotel)) {
      types.add('hotel');
    }

    if (trip.cities?.some((city) => city.needTransportation)) {
      types.add('transportation');
    }

    (trip.costs ?? []).forEach((cost) => {
      const mapped = mapApiCostType(cost.costType);
      if (mapped) {
        types.add(mapped);
      }
    });

    types.add('other');

    return types;
  }, [trip.cities, trip.costs]);

  const categories = useMemo(
    () => CATEGORY_ORDER.filter((category) => requiredTypes.has(category)),
    [requiredTypes]
  );

  const [activeForm, setActiveForm] = useState<CostFormState | null>(null);
  const [costToDelete, setCostToDelete] = useState<TripDetailCost | null>(null);
  const [removedDocumentIds, setRemovedDocumentIds] = useState<number[]>([]);
  const [newFiles, setNewFiles] = useState<UploadCandidate[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [expandedCostIds, setExpandedCostIds] = useState<Set<number>>(new Set());
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const highlightSet = useMemo(() => new Set(highlightCategories ?? []), [highlightCategories]);

  const toggleCostExpanded = useCallback((costId: number) => {
    setExpandedCostIds((prev) => {
      const next = new Set(prev);
      if (next.has(costId)) {
        next.delete(costId);
      } else {
        next.add(costId);
      }
      return next;
    });
  }, []);

  const form = useForm<CostFormValues>({
    defaultValues: {
      comment: '',
    },
  });

  const resetFormState = useCallback(() => {
    form.reset({ comment: '' });
    form.clearErrors();
    setNewFiles([]);
    setRemovedDocumentIds([]);
    setFormError(null);
  }, [form]);

  const closeForm = useCallback(() => {
    setActiveForm(null);
    resetFormState();
  }, [resetFormState]);

  const isBusy =
    createCostMutation.isPending ||
    updateCostMutation.isPending ||
    deleteCostMutation.isPending ||
    uploadDocumentsMutation.isPending ||
    deleteDocumentMutation.isPending;

  const handleAddFiles = (files: FileList | null) => {
    if (!files) {
      return;
    }

    setFormError(null);

    const next: UploadCandidate[] = [];
    Array.from(files).forEach((file, index) => {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        next.push({
          file,
          id: generateUploadId(file, index),
          error: tErrors('fileTooLarge'),
        });
        return;
      }

      const normalizedName = file.name.toLowerCase();
      const hasAllowedExtension = ACCEPTED_FILE_EXTENSIONS.some((extension) =>
        normalizedName.endsWith(extension)
      );
      const hasAllowedMimeType =
        !file.type || ACCEPTED_MIME_TYPES.includes(file.type.toLowerCase());

      if (!hasAllowedMimeType && !hasAllowedExtension) {
        next.push({
          file,
          id: generateUploadId(file, index),
          error: tErrors('unsupportedFileType'),
        });
        return;
      }

      next.push({
        file,
        id: generateUploadId(file, index),
      });
    });

    setNewFiles((prev) => [...prev, ...next]);
  };

  const handleRemoveExistingDocument = (documentId: number) => {
    setRemovedDocumentIds((prev) => (prev.includes(documentId) ? prev : [...prev, documentId]));
  };

  const handleRemovePendingFile = (id: string) => {
    setNewFiles((prev) => prev.filter((candidate) => candidate.id !== id));
  };

  const handleSubmit = async (values: CostFormValues) => {
    if (!activeForm) {
      return;
    }
    const targetCost = activeForm.cost;
    setFormError(null);
    form.clearErrors();
    const currentDocuments = targetCost?.documents ?? [];
    const keptDocuments = currentDocuments.filter(
      (document) => !removedDocumentIds.includes(document.id)
    );

    const totalDocuments = keptDocuments.length + newFiles.filter((item) => !item.error).length;

    if (totalDocuments === 0) {
      setFormError(tErrors('documentsRequired'));
      return;
    }

    const trimmedComment = values.comment.trim();

    let uploadedDocuments: TripDetailCostDocument[] = [];
    const removedDocumentsSnapshot = [...removedDocumentIds];

    try {
      const validNewFiles = newFiles.filter((item) => !item.error).map((item) => item.file);

      if (validNewFiles.length > 0) {
        uploadedDocuments = await uploadDocumentsMutation.mutateAsync({ files: validNewFiles });
      }

      const payload: CostPayload = {
        costType:
          activeForm.mode === 'edit' && targetCost
            ? (targetCost.costType as CostType)
            : (activeForm.type as CostType),
        actualCost: targetCost?.actualCost ?? 0,
        currencyId: targetCost?.currencyId ?? DEFAULT_CURRENCY_ID,
        documentIds: [
          ...keptDocuments.map((document) => document.id),
          ...uploadedDocuments.map((document) => document.id),
        ],
        comment: trimmedComment.length > 0 ? trimmedComment : undefined,
      };

      if (activeForm.mode === 'create') {
        await createCostMutation.mutateAsync({ tripId, payload });
      } else if (targetCost) {
        await updateCostMutation.mutateAsync({
          costId: targetCost.id,
          tripId,
          payload,
        });
      }

      closeForm();

      if (removedDocumentsSnapshot.length > 0) {
        removedDocumentsSnapshot.forEach((documentId) => {
          deleteDocumentMutation.mutate({ documentId });
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setFormError(message);
      if (uploadedDocuments.length > 0) {
        uploadedDocuments.forEach((document) => {
          deleteDocumentMutation.mutate({ documentId: document.id });
        });
      }
    }
  };

  const openCreateForm = (type: VisibleCostCategory) => {
    setActiveForm({
      mode: 'create',
      type,
    });
    resetFormState();
  };

  const openEditForm = (type: VisibleCostCategory, cost: TripDetailCost) => {
    setActiveForm({
      mode: 'edit',
      type,
      cost,
    });
    form.reset({
      comment: cost.comment ?? '',
    });
    setNewFiles([]);
    setRemovedDocumentIds([]);
    setFormError(null);
  };

  const onSubmit = form.handleSubmit(handleSubmit);

  const renderDocumentCard = ({
    key,
    documentId,
    name,
    sizeHuman,
    sizeBytes,
    errorMessage,
    onRemove,
    onView,
    onDownload,
    isViewLoading,
    isDownloadLoading,
    disabled,
  }: {
    key: string | number;
    documentId?: number;
    name: string;
    sizeHuman?: string;
    sizeBytes?: number;
    errorMessage?: string;
    onRemove?: () => void;
    onView?: () => void;
    onDownload?: () => void;
    isViewLoading?: boolean;
    isDownloadLoading?: boolean;
    disabled?: boolean;
  }) => {
    const displaySize = sizeHuman ?? (sizeBytes != null ? formatBytes(sizeBytes) : undefined);
    const hasError = Boolean(errorMessage);
    const hasActions = onRemove || onView || onDownload;
    const isActionLoading = isViewLoading || isDownloadLoading;

    return (
      <div
        key={key}
        className={`flex flex-col gap-3 rounded-[16px] border px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between ${
          hasError ? 'border-[#e5484d] bg-[#ffe2e5]' : 'border-[#f1f1f1] bg-white'
        }`}
      >
        <div className="flex min-w-0 flex-col gap-1">
          <span className="max-w-[220px] truncate text-[15px] leading-[22px] font-medium text-[#292929] sm:max-w-[320px]">
            {name}
          </span>
          {displaySize ? (
            <span
              className={`text-[13px] leading-5 ${hasError ? 'text-[#b91c1c]' : 'text-[#666666]'}`}
            >
              {displaySize}
            </span>
          ) : null}
          {errorMessage ? (
            <span className="text-[13px] leading-5 text-[#e5484d]">{errorMessage}</span>
          ) : null}
        </div>

        {hasActions ? (
          <div className="flex items-center gap-2">
            {onView && documentId ? (
              <Button
                type="button"
                variant="unstyled"
                size="none"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#f5f5f5] p-2 transition-colors hover:bg-[#e5e5e5] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#171717] disabled:opacity-50"
                onClick={onView}
                disabled={disabled || isActionLoading}
                aria-label={t('actions.view')}
              >
                {isViewLoading ? (
                  <Spinner variant="circle" size={20} className="text-[#666666]" />
                ) : (
                  <img src="/assets/icons/document-view.svg" alt="" className="h-5 w-5" />
                )}
              </Button>
            ) : null}
            {onDownload && documentId ? (
              <Button
                type="button"
                variant="unstyled"
                size="none"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#f5f5f5] p-2 transition-colors hover:bg-[#e5e5e5] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#171717] disabled:opacity-50"
                onClick={onDownload}
                disabled={disabled || isActionLoading}
                aria-label={t('actions.download')}
              >
                {isDownloadLoading ? (
                  <Spinner variant="circle" size={20} className="text-[#666666]" />
                ) : (
                  <img src="/assets/icons/download.svg" alt="" className="h-5 w-5" />
                )}
              </Button>
            ) : null}
            {onRemove ? (
              <Button
                type="button"
                variant="unstyled"
                size="none"
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#fff2f2] p-2 transition-colors ${
                  hasError ? 'hover:bg-[#ffc9cf]' : 'hover:bg-[#ffe2e5]'
                } focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#171717] disabled:opacity-50`}
                onClick={onRemove}
                disabled={disabled || isActionLoading}
                aria-label={t('actions.delete')}
              >
                <img src="/assets/icons/delete.svg" alt="" className="h-5 w-5" />
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  const renderFormSection = (
    mode: 'create' | 'edit',
    type: VisibleCostCategory,
    cost?: TripDetailCost
  ) => {
    const { labelKey, icon } = CATEGORY_CONFIG[type];
    const label = tCategories(labelKey);
    const existingDocuments =
      cost?.documents.filter((document) => !removedDocumentIds.includes(document.id)) ?? [];
    const inputId = `document-upload-${mode}-${type}${cost ? `-${cost.id}` : ''}`;

    return (
      <Form {...form}>
        <form onSubmit={onSubmit} className="rounded-[16px] border border-[#ebebeb] bg-white">
          <div className="flex flex-col gap-4 border-b border-[#f1f1f1] px-[14px] py-[20px] sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex shrink-0 items-center justify-center rounded-full border border-[#f1f1f1] bg-[#f9f9f9] p-2">
                <img src={icon} alt="" className="h-6 w-6" />
              </div>
              <div className="flex min-w-0 flex-col">
                <span className="text-[18px] leading-7 font-medium text-[#292929]">{label}</span>
                {mode === 'edit' && cost?.costTypeLabel ? (
                  <span className="text-[14px] leading-5 text-[#666666]">{cost.costTypeLabel}</span>
                ) : null}
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Button
                type="button"
                variant="ghost"
                className="w-full rounded-[30px] border border-[#ebebeb] px-4 py-2 text-[14px] leading-5 text-[#292929] sm:w-auto"
                onClick={closeForm}
                disabled={isBusy}
              >
                {tForm('cancel')}
              </Button>
              <Button
                type="submit"
                variant="black"
                className="flex w-full items-center justify-center gap-2 rounded-[30px] px-6 py-2 text-[14px] leading-5 font-medium sm:w-auto"
                disabled={isBusy}
              >
                {isBusy ? (
                  <Spinner variant="circle" className="text-white" />
                ) : mode === 'edit' ? (
                  t('actions.saveChanges')
                ) : (
                  t('actions.save')
                )}
              </Button>
            </div>
          </div>

          <div className="space-y-4 px-[14px] py-[20px]">
            <div>
              <label
                htmlFor={inputId}
                className="mb-2 block text-[14px] leading-5 font-medium text-[#292929]"
              >
                {tForm('documentsLabel')}
              </label>
              <div className="rounded-[16px] border border-dashed border-[#bbbbbb] bg-white px-4 py-6 text-center">
                <div className="mb-3 flex justify-center">
                  <Button
                    type="button"
                    variant="unstyled"
                    size="none"
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#171717] text-white transition-colors hover:bg-[#292929] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#171717]"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isBusy}
                    aria-label={tForm('uploader.button')}
                  >
                    <img
                      src="/assets/icons/plus.svg"
                      alt=""
                      className="h-6 w-6"
                      style={{ filter: 'brightness(0) invert(1)' }}
                    />
                  </Button>
                </div>
                <p className="text-sm leading-5 font-medium text-[#292929]">
                  {tForm('uploader.title')}
                </p>
                <p className="text-sm leading-5 text-[#666666]">
                  {tForm('uploader.hint', { size: '50MB' })}
                </p>
                <p className="text-sm leading-5 text-[#666666]">
                  {tForm('uploader.types', { types: ACCEPTED_FILE_EXTENSIONS.join(', ') })}
                </p>
                <input
                  id={inputId}
                  type="file"
                  className="hidden"
                  ref={fileInputRef}
                  multiple
                  onChange={(event) => {
                    handleAddFiles(event.target.files);
                    event.target.value = '';
                  }}
                  accept={[...ACCEPTED_FILE_EXTENSIONS, ...ACCEPTED_MIME_TYPES].join(',')}
                />
              </div>
              {formError ? (
                <p className="mt-2 text-sm leading-5 text-[#e5484d]">{formError}</p>
              ) : null}
            </div>

            {existingDocuments.length > 0 && (
              <div className="space-y-3">
                <div className="space-y-3">
                  {existingDocuments.map((document) =>
                    renderDocumentCard({
                      key: document.id,
                      documentId: document.id,
                      name: document.name,
                      sizeHuman: document.sizeHuman,
                      sizeBytes: document.size,
                      onView: () => viewDocument(document.id, document.name),
                      onDownload: () => downloadDocument(document.id, document.name),
                      onRemove: () => handleRemoveExistingDocument(document.id),
                      isViewLoading: isDocumentLoading(document.id, 'view'),
                      isDownloadLoading: isDocumentLoading(document.id, 'download'),
                      disabled: isBusy || isAnyDocumentLoading,
                    })
                  )}
                </div>
              </div>
            )}

            {newFiles.length > 0 && (
              <div className="space-y-3">
                <div className="space-y-3">
                  {newFiles.map((candidate) =>
                    renderDocumentCard({
                      key: candidate.id,
                      name: candidate.file.name,
                      sizeBytes: candidate.file.size,
                      errorMessage: candidate.error,
                      onRemove: () => handleRemovePendingFile(candidate.id),
                      disabled: isBusy,
                    })
                  )}
                </div>
              </div>
            )}

            <FormField
              control={form.control}
              name="comment"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <textarea
                      {...field}
                      rows={4}
                      placeholder={tForm('commentPlaceholder')}
                      aria-label={tForm('commentPlaceholder')}
                      className="w-full rounded-[12px] border border-[#ebebeb] bg-white px-3 py-2 text-[14px] leading-5 text-[#292929] transition-shadow outline-none focus:border-[#292929] focus:shadow-[0_0_0_1px_rgba(41,41,41,0.08)] disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={isBusy}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </form>
      </Form>
    );
  };

  const renderEmptyCategory = (type: VisibleCostCategory) => {
    if (activeForm?.mode === 'create' && activeForm.type === type) {
      return renderFormSection('create', type);
    }

    const { labelKey, icon } = CATEGORY_CONFIG[type];
    const label = tCategories(labelKey);
    const isHighlighted = highlightSet.has(type);
    const containerClasses = `rounded-[16px] border px-[14px] py-[20px] ${
      isHighlighted ? 'border-[#e5484d] bg-[#fff5f5]' : 'border-[#ebebeb] bg-white'
    }`;

    return (
      <div className={containerClasses}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex shrink-0 items-center justify-center rounded-full border border-[#f1f1f1] bg-[#f9f9f9] p-2">
              <img src={icon} alt="" className="h-6 w-6" />
            </div>
            <span className="min-w-0 text-[18px] leading-7 font-medium text-[#292929]">
              {label}
            </span>
          </div>

          {canManage && (
            <Button
              type="button"
              variant="black"
              className="flex w-full items-center justify-center gap-2 rounded-[30px] px-4 py-2 text-[14px] leading-5 font-medium sm:w-auto"
              onClick={() => openCreateForm(type)}
              disabled={isBusy}
            >
              <img src="/assets/icons/document-add.svg" alt="" className="h-5 w-5" />
              <span>{t('actions.upload')}</span>
            </Button>
          )}
        </div>
        {isHighlighted ? (
          <p className="mt-3 text-[13px] leading-5 text-[#e5484d]">
            {tErrors('documentsRequired')}
          </p>
        ) : null}
      </div>
    );
  };

  const renderFilledCard = (type: VisibleCostCategory, cost: TripDetailCost) => {
    const { labelKey, icon } = CATEGORY_CONFIG[type];
    const label = tCategories(labelKey);
    const isEditing =
      activeForm?.mode === 'edit' && activeForm.type === type && activeForm.cost?.id === cost.id;

    if (isEditing) {
      return renderFormSection('edit', type, cost);
    }

    const documents = cost.documents ?? [];
    const hasDocuments = documents.length > 0;
    const isExpanded = expandedCostIds.has(cost.id);

    return (
      <div className="rounded-[16px] border border-[#ebebeb] bg-[#f9f9f9] px-[14px] py-[20px]">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex shrink-0 items-center justify-center rounded-full border border-[#f1f1f1] bg-[#f9f9f9] p-2">
              <img src={icon} alt="" className="h-6 w-6" />
            </div>
            <div className="flex min-w-0 flex-col">
              <span className="text-[18px] leading-7 font-medium text-[#292929]">{label}</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {hasDocuments && (
              <Button
                type="button"
                variant="unstyled"
                size="none"
                className="flex items-center gap-1.5 rounded-full border border-[#f1f1f1] bg-white px-3 py-2 text-[13px] leading-5 text-[#666666] hover:bg-[#f5f5f5]"
                onClick={() => toggleCostExpanded(cost.id)}
                aria-expanded={isExpanded}
                aria-label={isExpanded ? t('actions.hideDocuments') : t('actions.showDocuments')}
              >
                <span>{t('labels.documentsCount', { count: documents.length })}</span>
                <img
                  src="/assets/icons/chevron-down.svg"
                  alt=""
                  className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                />
              </Button>
            )}
            {canManage && (
              <>
                <Button
                  type="button"
                  variant="unstyled"
                  size="none"
                  className="shrink-0 rounded-full border border-[#f1f1f1] bg-white p-2 hover:bg-[#f5f5f5]"
                  onClick={() => openEditForm(type, cost)}
                  disabled={isBusy}
                  aria-label={t('actions.edit')}
                >
                  <img src="/assets/icons/edit.svg" alt="" className="h-5 w-5" />
                </Button>
                <Button
                  type="button"
                  variant="unstyled"
                  size="none"
                  className="shrink-0 rounded-full border border-[#f1f1f1] bg-white p-2 hover:bg-[#fff2f2]"
                  onClick={() => setCostToDelete(cost)}
                  disabled={isBusy}
                  aria-label={t('actions.delete')}
                >
                  <img src="/assets/icons/delete.svg" alt="" className="h-5 w-5" />
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Collapsible documents section */}
        {hasDocuments && isExpanded && (
          <div className="mt-4 space-y-3 border-t border-[#ebebeb] pt-4">
            {documents.map((document) =>
              renderDocumentCard({
                key: document.id,
                documentId: document.id,
                name: document.name,
                sizeHuman: document.sizeHuman,
                sizeBytes: document.size,
                onView: () => viewDocument(document.id, document.name),
                onDownload: () => downloadDocument(document.id, document.name),
                isViewLoading: isDocumentLoading(document.id, 'view'),
                isDownloadLoading: isDocumentLoading(document.id, 'download'),
                disabled: isAnyDocumentLoading,
              })
            )}
            {cost.comment && (
              <div className="rounded-[12px] border border-[#f1f1f1] bg-white px-4 py-3">
                <span className="text-[13px] leading-5 text-[#666666]">{cost.comment}</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderCategory = (type: VisibleCostCategory) => {
    const costs = costsByType[type] ?? [];

    if (costs.length === 0) {
      return renderEmptyCategory(type);
    }

    const isCreateActive = activeForm?.mode === 'create' && activeForm.type === type;

    return (
      <div className="space-y-3">
        {costs.map((cost) => (
          <div key={cost.id}>{renderFilledCard(type, cost)}</div>
        ))}
        {isCreateActive ? renderFormSection('create', type) : null}
        {type === 'other' && canManage && !isCreateActive && !activeForm && (
          <Button
            type="button"
            variant="black"
            className="flex items-center gap-2 rounded-[30px] px-4 py-2 text-[14px] leading-5 font-medium"
            onClick={() => openCreateForm(type)}
            disabled={isBusy}
          >
            <img src="/assets/icons/document-add.svg" alt="" className="h-5 w-5" />
            <span>{t('actions.upload')}</span>
          </Button>
        )}
      </div>
    );
  };

  const handleConfirmDelete = async () => {
    if (!costToDelete) {
      return;
    }

    try {
      await deleteCostMutation.mutateAsync({ costId: costToDelete.id, tripId });
      if (activeForm?.mode === 'edit' && activeForm.cost?.id === costToDelete.id) {
        closeForm();
      }
      if (costToDelete.documents.length > 0) {
        costToDelete.documents.forEach((document) => {
          deleteDocumentMutation.mutate({ documentId: document.id });
        });
      }
      setCostToDelete(null);
    } catch (error) {
      console.error('Failed to delete cost', error);
    }
  };

  return (
    <>
      <div className="rounded-[24px] border border-[#f1f1f1] bg-white p-4 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)] md:p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg leading-7 font-medium text-black">{t('title')}</h2>
        </div>

        <div className="flex flex-col gap-4">
          {categories.map((category) => (
            <div key={category}>{renderCategory(category)}</div>
          ))}
        </div>
      </div>

      <ConfirmationDialog
        isOpen={Boolean(costToDelete)}
        onClose={() => setCostToDelete(null)}
        onConfirm={handleConfirmDelete}
        title={t('deleteDialog.title')}
        message={t('deleteDialog.message')}
        confirmText={t('deleteDialog.confirm')}
        cancelText={t('deleteDialog.cancel')}
        confirmButtonVariant="danger"
        isLoading={deleteCostMutation.isPending}
      />

      {removedDocumentIds.length > 0 && (
        <div className="sr-only" aria-live="polite">
          {t('accessibility.removedDocuments', { count: removedDocumentIds.length })}
        </div>
      )}
    </>
  );
}
